import { useEffect, useRef, type ReactNode } from 'react'
import { clsx } from 'clsx'

export interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'
  className?: string
  /** When true, clicking the overlay or pressing Escape will not close the modal */
  persistent?: boolean
}

export function Modal({
  open,
  onClose,
  children,
  title,
  description,
  size = 'md',
  className,
  persistent = false,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !persistent) onClose()
    }

    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, onClose, persistent])

  if (!open) return null

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    fullscreen: 'max-w-none h-full w-full',
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current && !persistent) onClose()
  }

  return (
    <div
      ref={overlayRef}
      className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/50',
        size !== 'fullscreen' && 'p-4'
      )}
      onClick={handleOverlayClick}
    >
      <div
        className={clsx(
          'w-full bg-white shadow-xl',
          size === 'fullscreen' ? 'rounded-none flex flex-col' : 'rounded-xl',
          sizes[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {(title || description) && (
          <div className="px-6 pt-6">
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold text-neutral-900">
                {title}
              </h2>
            )}
            {description && <p className="mt-1 text-sm text-neutral-500">{description}</p>}
          </div>
        )}
        <div className={clsx('p-6', size === 'fullscreen' && 'flex-1 overflow-auto')}>
          {children}
        </div>
      </div>
    </div>
  )
}

export interface ModalFooterProps {
  children: ReactNode
  className?: string
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div className={clsx('flex items-center justify-end gap-3 pt-4', className)}>{children}</div>
  )
}
